// Shared Chart.js configuration following BodyOS design system
// Colors synced with docs/style-guide/tokens.json
// Using grayscale/neutral palette for isometric aesthetic

export const colors = {
  // Neutral grayscale palette - Primary chart colors
  neutral: {
    white: '#ffffff',
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
    black: '#000000'
  },

  // Transparent variants for fills (using neutral-900)
  neutralAlpha: {
    5: 'rgba(23, 23, 23, 0.05)',
    10: 'rgba(23, 23, 23, 0.1)',
    20: 'rgba(23, 23, 23, 0.2)',
    30: 'rgba(23, 23, 23, 0.3)',
    40: 'rgba(23, 23, 23, 0.4)',
    50: 'rgba(23, 23, 23, 0.5)'
  },

  // Chart-specific palette (distinct shades for multi-series)
  chart: [
    '#171717', // neutral-900 - primary
    '#525252', // neutral-600 - secondary
    '#a3a3a3', // neutral-400 - tertiary
    '#d4d4d4', // neutral-300 - quaternary
    '#404040', // neutral-700 - quinary
    '#737373' // neutral-500 - senary
  ],

  // Legacy aliases (mapped to neutral)
  primary: '#171717',
  primaryLight: 'rgba(23, 23, 23, 0.1)',
  secondary: '#525252',
  secondaryLight: 'rgba(82, 82, 82, 0.1)',
  accent: '#171717',
  accentLight: 'rgba(23, 23, 23, 0.1)',

  // Backwards compatibility - map purple references to neutral grayscale
  purple: {
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626'
  },
  purpleAlpha: {
    10: 'rgba(23, 23, 23, 0.1)',
    20: 'rgba(23, 23, 23, 0.2)'
  },

  // Semantic colors (minimal use - prefer grayscale)
  success: '#171717',
  successLight: 'rgba(23, 23, 23, 0.1)',
  warning: '#525252',
  warningLight: 'rgba(82, 82, 82, 0.1)',
  danger: '#171717',
  dangerLight: 'rgba(23, 23, 23, 0.1)',
  info: '#404040',
  infoLight: 'rgba(64, 64, 64, 0.1)',

  // Grid and text colors
  grid: '#e5e5e5',
  gridLight: '#f5f5f5',
  text: '#171717',
  textMuted: '#737373',
  textLight: '#a3a3a3'
}

export const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: '#171717',
      titleColor: '#ffffff',
      titleFont: {
        family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
        size: 11,
        weight: 'bold'
      },
      bodyColor: '#e5e5e5',
      bodyFont: {
        family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
        size: 11
      },
      padding: 12,
      cornerRadius: 0,
      borderColor: '#171717',
      borderWidth: 2,
      displayColors: false
    }
  },
  scales: {
    x: {
      grid: {
        display: false,
        drawBorder: true,
        borderColor: '#171717',
        borderWidth: 2
      },
      ticks: {
        color: '#525252',
        font: {
          family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
          size: 10
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: '#e5e5e5',
        drawBorder: true,
        borderColor: '#171717',
        borderWidth: 2
      },
      ticks: {
        color: '#525252',
        font: {
          family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
          size: 10
        },
        precision: 0
      }
    }
  }
}

export const lineChartOptions = {
  ...defaultOptions,
  elements: {
    line: {
      tension: 0, // Sharp angular lines for isometric aesthetic
      borderWidth: 2,
      borderColor: '#171717'
    },
    point: {
      radius: 0,
      hoverRadius: 6,
      hoverBorderWidth: 2,
      hoverBackgroundColor: '#ffffff',
      hoverBorderColor: '#171717'
    }
  }
}

export const barChartOptions = {
  ...defaultOptions,
  plugins: {
    ...defaultOptions.plugins,
    legend: { display: false }
  },
  elements: {
    bar: {
      borderWidth: 2,
      borderColor: '#171717',
      backgroundColor: '#171717'
    }
  }
}

export const doughnutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'right',
      labels: {
        usePointStyle: false,
        boxWidth: 12,
        boxHeight: 12,
        padding: 16,
        color: '#171717',
        font: {
          family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
          size: 11
        }
      }
    },
    tooltip: {
      backgroundColor: '#171717',
      bodyColor: '#e5e5e5',
      bodyFont: {
        family: "'JetBrains Mono', 'Space Mono', 'Courier New', monospace",
        size: 11
      },
      padding: 12,
      cornerRadius: 0,
      borderColor: '#171717',
      borderWidth: 2
    }
  },
  cutout: '60%',
  elements: {
    arc: {
      borderWidth: 2,
      borderColor: '#ffffff'
    }
  }
}

// Area chart with gradient fill
export const areaChartOptions = {
  ...lineChartOptions,
  elements: {
    ...lineChartOptions.elements,
    line: {
      tension: 0,
      borderWidth: 2,
      borderColor: '#171717',
      fill: true,
      backgroundColor: 'rgba(23, 23, 23, 0.1)'
    }
  }
}

// Format date labels for display (e.g., "Dec 15")
export function formatDateLabel(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
}

// Reduce labels for large datasets
export function reduceLabels(labels, maxLabels = 10) {
  if (labels.length <= maxLabels) return labels

  const step = Math.ceil(labels.length / maxLabels)
  return labels.map((label, i) => (i % step === 0 ? formatDateLabel(label) : ''))
}

// Create gradient for area charts
export function createGradient(ctx, height) {
  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, 'rgba(23, 23, 23, 0.2)')
  gradient.addColorStop(1, 'rgba(23, 23, 23, 0)')
  return gradient
}
